/**
 * iter-198 (Feb 2026) — Admin App Version Control (Force-Update panel).
 *
 * Route: /control/asb606-admin/app-versions
 * Writes to `app_version_config` collection. The mobile Admin App polls
 * `/api/app/version-check` on every launch and blocks itself if a newer
 * version is required or its version_code is in `blocked_version_codes`.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Smartphone, Upload, Save, Ban, CheckCircle,
  AlertTriangle, RefreshCw, Plus, X,
} from 'lucide-react';

import DashboardHeader from '../components/DashboardHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  platform: 'android',
  latest_version_name: '',
  latest_version_code: 1,
  min_supported_version_code: 1,
  blocked_version_codes: [],
  force_update: true,
  apk_url: '',
  store_url: '',
  message_ar: 'تم إيقاف هذا الإصدار. حمّل الإصدار الجديد للمتابعة.',
  message_en: 'This version has been discontinued. Please download the new version.',
};

export default function AppVersionsControlPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [platform, setPlatform] = useState('android');
  const [config, setConfig] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newBlockCode, setNewBlockCode] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/app-versions/${platform}`,
        { headers: getAuthHeaders() },
      );
      setConfig({ ...EMPTY, ...r.data });
    } catch (_e) {
      toast.error(t('فشل تحميل الإعدادات', 'Failed to load config'));
    } finally {
      setLoading(false);
    }
  }, [platform]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!config.latest_version_name.trim()) {
      toast.error(t('أدخل اسم الإصدار الأحدث', 'Enter the latest version name'));
      return;
    }
    if (!config.apk_url.trim() && !config.store_url.trim()) {
      toast.error(t('يجب إدخال رابط APK أو رابط المتجر', 'You must provide either an APK URL or a store URL'));
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...config,
        platform,
        latest_version_code: Number(config.latest_version_code) || 1,
        min_supported_version_code: Number(config.min_supported_version_code) || 1,
        blocked_version_codes: (config.blocked_version_codes || []).map(Number),
      };
      const r = await axios.put(
        `${API_URL}/api/admin/app-versions/${platform}`,
        body,
        { headers: getAuthHeaders() },
      );
      setConfig({ ...EMPTY, ...r.data });
      toast.success(t('تم حفظ الإعدادات — يسري فوراً على تطبيقات المستخدمين',
                     'Saved — takes effect on the next mobile app launch'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const addBlockCode = async () => {
    const code = Number(newBlockCode);
    if (!code || code < 1) {
      toast.error(t('أدخل رقم إصدار صالح', 'Enter a valid version code'));
      return;
    }
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/app-versions/${platform}/toggle-block`,
        { version_code: code, blocked: true },
        { headers: getAuthHeaders() },
      );
      setConfig({ ...EMPTY, ...r.data });
      setNewBlockCode('');
      toast.success(t(`تم إيقاف الإصدار ${code}`, `Blocked version ${code}`));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الإيقاف', 'Block failed'));
    }
  };

  const unblockCode = async (code) => {
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/app-versions/${platform}/toggle-block`,
        { version_code: code, blocked: false },
        { headers: getAuthHeaders() },
      );
      setConfig({ ...EMPTY, ...r.data });
      toast.success(t(`تم تفعيل الإصدار ${code}`, `Unblocked version ${code}`));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الإلغاء', 'Unblock failed'));
    }
  };

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}
         data-testid="app-versions-control-page">
      <DashboardHeader showBackButton backPath="/dashboard" />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Title */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
              <Smartphone className="w-6 h-6 text-[#d4af37]" />
              {t('التحكم بتطبيق الأدمن', 'Admin App Control')}
            </h1>
            <p className="text-white/50 text-sm mt-1 max-w-2xl">
              {t('أوقف أي إصدار قديم عن العمل، وحمِّل رابط الإصدار الجديد ليظهر تلقائياً لكل مستخدم يحاول فتح التطبيق.',
                 'Discontinue any old build and publish the new APK — every user on an older version is forced to update on next launch.')}
            </p>
          </div>
          <Button variant="outline" onClick={load}
                  className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
                  data-testid="app-versions-refresh">
            <RefreshCw className={`w-4 h-4 me-2 ${loading ? 'animate-spin' : ''}`} />
            {t('تحديث', 'Refresh')}
          </Button>
        </div>

        {/* Platform switcher */}
        <div className="flex gap-2">
          {['android', 'ios'].map(p => (
            <button key={p} onClick={() => setPlatform(p)}
                    className={`px-4 py-2 rounded-md text-sm font-bold transition ${
                      platform === p
                        ? 'bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/40'
                        : 'bg-white/5 text-white/60 border border-white/10 hover:border-white/20'
                    }`}
                    data-testid={`platform-tab-${p}`}>
              {p === 'android' ? 'Android' : 'iOS'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        ) : (
          <>
            {/* Current version + APK URL */}
            <Section title={t('الإصدار الحالي', 'Current version')}
                     Icon={CheckCircle}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label={t('اسم الإصدار (Version Name)', 'Version name')}>
                  <Input value={config.latest_version_name}
                         onChange={(e) => setConfig({ ...config, latest_version_name: e.target.value })}
                         placeholder="e.g. 0.02"
                         data-testid="latest-version-name" />
                </Field>
                <Field label={t('رقم الإصدار (Version Code)', 'Version code')}>
                  <Input type="number" min={1}
                         value={config.latest_version_code}
                         onChange={(e) => setConfig({ ...config, latest_version_code: e.target.value })}
                         data-testid="latest-version-code" />
                </Field>
                <Field label={t('أدنى إصدار مدعوم (Min Supported)', 'Min supported version code')}>
                  <Input type="number" min={1}
                         value={config.min_supported_version_code}
                         onChange={(e) => setConfig({ ...config, min_supported_version_code: e.target.value })}
                         data-testid="min-supported-version" />
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <Field label={t('رابط تحميل APK (Direct URL)', 'Direct APK download URL')}>
                  <Input value={config.apk_url}
                         onChange={(e) => setConfig({ ...config, apk_url: e.target.value })}
                         placeholder="https://.../CloudGM-Admin-v0.02.apk"
                         data-testid="apk-url" />
                </Field>
                <Field label={t('رابط المتجر (اختياري)', 'Store URL (optional)')}>
                  <Input value={config.store_url}
                         onChange={(e) => setConfig({ ...config, store_url: e.target.value })}
                         placeholder="https://play.google.com/store/apps/details?id=..."
                         data-testid="store-url" />
                </Field>
              </div>
              <div className="mt-3 flex items-center justify-between bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2.5">
                <div>
                  <div className="text-white font-bold text-sm">
                    {t('تحديث إجباري (Force Update)', 'Force Update')}
                  </div>
                  <div className="text-[11px] text-white/50">
                    {t('عند التفعيل: أي إصدار أقدم من الأحدث يُجبَر على التحديث.',
                       'When ON: any older version is forced to update on launch.')}
                  </div>
                </div>
                <Switch checked={config.force_update}
                        onCheckedChange={(v) => setConfig({ ...config, force_update: v })}
                        data-testid="force-update-switch" />
              </div>
            </Section>

            {/* Blocked versions manager */}
            <Section title={t('الإصدارات الموقوفة', 'Blocked versions')}
                     Icon={Ban}>
              <div className="flex gap-2 mb-3">
                <Input type="number" min={1}
                       value={newBlockCode}
                       onChange={(e) => setNewBlockCode(e.target.value)}
                       placeholder={t('أدخل version_code لإيقافه', 'Enter version_code to block')}
                       className="flex-1"
                       data-testid="new-block-code" />
                <Button onClick={addBlockCode}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="add-block-code">
                  <Ban className="w-4 h-4 me-2" />
                  {t('إيقاف', 'Block')}
                </Button>
              </div>
              {(config.blocked_version_codes || []).length === 0 ? (
                <div className="text-white/40 text-xs">
                  {t('لا يوجد إصدارات موقوفة.', 'No blocked versions.')}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {config.blocked_version_codes.map(code => (
                    <div key={code}
                         className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/25 text-red-200 rounded-lg px-3 py-1.5 text-sm">
                      <span className="font-mono font-bold">v{code}</span>
                      <button onClick={() => unblockCode(code)}
                              className="p-1 rounded hover:bg-red-500/20"
                              title={t('إلغاء الإيقاف', 'Unblock')}
                              data-testid={`unblock-code-${code}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Update dialog messages */}
            <Section title={t('رسائل التحديث', 'Update dialog messages')}
                     Icon={AlertTriangle}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label={t('الرسالة بالعربية', 'Arabic message')}>
                  <Textarea value={config.message_ar}
                            onChange={(e) => setConfig({ ...config, message_ar: e.target.value })}
                            rows={3}
                            dir="rtl"
                            data-testid="message-ar" />
                </Field>
                <Field label={t('English message', 'English message')}>
                  <Textarea value={config.message_en}
                            onChange={(e) => setConfig({ ...config, message_en: e.target.value })}
                            rows={3}
                            dir="ltr"
                            data-testid="message-en" />
                </Field>
              </div>
            </Section>

            {/* Save + Metadata */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-white/40">
                {config.updated_at && (
                  <>
                    {t('آخر تحديث:', 'Last updated:')}{' '}
                    {new Date(config.updated_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB')}
                    {config.updated_by && ` — ${config.updated_by}`}
                  </>
                )}
              </div>
              <Button onClick={save} disabled={saving}
                      className="bg-[#d4af37] hover:bg-[#c39c30] text-black font-bold px-6"
                      data-testid="app-versions-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Save className="w-4 h-4 me-2" />}
                {t('حفظ الإعدادات', 'Save configuration')}
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Section({ title, Icon, children }) {
  return (
    <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-3">
      <h2 className="text-white font-bold text-sm inline-flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#d4af37]" />
        {title}
      </h2>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-white/70 text-[11px] mb-1">{label}</label>
      {children}
    </div>
  );
}
